<div id="body" role="main" class="cbsw-smart-widget" [attr.aria-label]="brandName + ' widget application'">
  @if (!localStorageaccessible) {
    <div
      class="cbsw-calenso-warnings p-2"
      role="alert"
      [attr.aria-live]="'polite'"
    >
      <app-alert
        isAlertMessageTranslated="true"
        type="warning"
        message="store_access_warning"
      />
    </div>
  }

  @if (isSmartWidgetLoading) {
    <div
      class="skeleton-box-card cbsw-smart-widget-loading"
      role="status"
      aria-live="polite"
      [attr.aria-label]="'Loading stores'"
    >
      <div role="list" [attr.aria-label]="'Loading smart widget'">
        <div
          class="d-flex stats-card"
          role="presentation"
          [attr.aria-hidden]="true"
        >
          <div class="skeleton-animation w-100 d-flex justify-content-center flex-column gap-3">
            <div class="bar bar-1"></div>
            <div class="bar bar-2"></div>
          </div>
        </div>
      </div>
    </div>
  }

  <div
    [ngClass]="{ 'd-none': isSmartWidgetLoading }"
  >
    @if (widgetService.widgetConf?.partner?.bookingwidget_enabled === 1) {
      @if (widgetService.widgetConf && widgetService.layoutType === 'CHATBOX') {
        <app-widget-layout>
          <div mainWidgetBody role="region" [attr.aria-label]="'Widget content'">
            @if (widgetService?.widgetConf?.type === widgetTypes.LEAD_GENERATOR) {
              <app-lead-generator />
            } @else if (widgetService?.widgetConf?.type === widgetTypes.SURVEY) {
              <app-survey />
            } @else if (widgetService?.widgetConf?.type === widgetTypes.BOOKING) {
              <app-booking />
            }
          </div>
        </app-widget-layout>
      }
  
      @if (['IFRAME', 'IFRAME_URL'].includes(widgetService.layoutType)) {
        <div class="container-fluid" role="region" [attr.aria-label]="'Widget container'">
          @if (widgetService.widgetConf) {
            @if (widgetService?.widgetConf?.type === widgetTypes.LEAD_GENERATOR) {
              <app-lead-generator />
            } @else if (widgetService?.widgetConf?.type === widgetTypes.SURVEY) {
              <app-survey />
            } @else if (widgetService?.widgetConf?.type === widgetTypes.BOOKING) {
              <app-booking />
            }
          } @else {
            <div
              class="d-flex flex-column justify-content-center align-items-center loader-container"
              role="status"
              [attr.aria-label]="'Loading widget'"
            >
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading widget content...</span>
              </div>
            </div>
          }
        </div>
      }
    } @else if (widgetService.widgetConf?.partner?.bookingwidget_enabled === 0 || isBookingWidgetDisabled || hasBootstrapError) {
      <!-- Reached from a successful bootstrap that reports the widget off
           (bookingwidget_enabled === 0), the JB-1637 403 booking_widget_disabled,
           or any other bootstrap failure (hasBootstrapError) where widgetConf is
           never set — same "booking currently unavailable" card instead of a blank.
           In CHATBOX layout it must render inside app-widget-layout (the floating
           chrome), exactly like the enabled content above; in iframe layout the
           card renders directly. -->
      @if (widgetService.layoutType === 'CHATBOX') {
        <app-widget-layout [openOnInit]="true">
          <div mainWidgetBody role="region" [attr.aria-label]="'Widget content'">
            <ng-container [ngTemplateOutlet]="unavailableCard"></ng-container>
          </div>
        </app-widget-layout>
      } @else {
        <ng-container [ngTemplateOutlet]="unavailableCard"></ng-container>
      }
    } @else if (isPartnerBlocked) {
      <!-- Partner is blocked or deleted: the configuration call was refused, so
           widgetConf is undefined and neither branch above can match. Same
           CHATBOX-vs-iframe rendering split as the unavailable branch above. -->
      @if (widgetService.layoutType === 'CHATBOX') {
        <app-widget-layout [openOnInit]="true">
          <div mainWidgetBody role="region" [attr.aria-label]="'Widget content'">
            <ng-container [ngTemplateOutlet]="blockedCard"></ng-container>
          </div>
        </app-widget-layout>
      } @else {
        <ng-container [ngTemplateOutlet]="blockedCard"></ng-container>
      }
    }
  </div>
</div>

<!-- Error-state cards, shared between the iframe (direct) and CHATBOX
     (inside app-widget-layout) render paths above. -->
<ng-template #unavailableCard>
  <div
    class="card card-warning bg-white rounded card-box m-3"
    role="alert"
    [attr.aria-live]="'polite'"
  >
    <div class="card-body">
      <div class="d-flex">
        <div class="text-center icon-block ps-1 pe-1" role="img" [attr.aria-label]="'Warning icon'">
          <div class="notification-icon">
            <img
              [src]="deployUrl + 'assets/images/notifications/warning_ico.svg'"
              alt="warning icon"
              aria-hidden="true"
            />
          </div>
        </div>
        <div class="content-block ps-3 pe-3">
          @if (hasBootstrapError) {
            <!-- Generic bootstrap failure (unknown 403 / 5xx / network) — a likely
                 transient problem, so "try again later" rather than the
                 "contact the provider" copy used for a deliberately-off widget. -->
            <div
              class="notification-title text-warning"
              role="heading"
              aria-level="2"
            >
              {{ 'error_messages.something_went_wrong' | translate }}
            </div>
          } @else {
            <div
              class="notification-title text-warning"
              role="heading"
              aria-level="2"
            >
              {{ 'error_messages.widget_disabled' | translate }}
            </div>
            <div class="notification-description mt-1">
              {{ 'error_messages.widget_disabled_description' | translate }}
            </div>
          }
        </div>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #blockedCard>
  <div
    class="card card-warning bg-white rounded card-box m-3"
    role="alert"
    [attr.aria-live]="'polite'"
  >
    <div class="card-body">
      <div class="d-flex">
        <div class="text-center icon-block ps-1 pe-1" role="img" [attr.aria-label]="'Warning icon'">
          <div class="notification-icon">
            <img
              [src]="deployUrl + 'assets/images/notifications/warning_ico.svg'"
              alt="warning icon"
              aria-hidden="true"
            />
          </div>
        </div>
        <div class="content-block ps-3 pe-3">
          <div
            class="notification-title text-warning"
            role="heading"
            aria-level="2"
          >
            {{ 'error_messages.booking_unavailable' | translate }}
          </div>
          <div class="notification-description mt-1">
            {{ 'error_messages.booking_unavailable_description' | translate }}
          </div>
        </div>
      </div>
    </div>
  </div>
</ng-template>
